"use client";

import { useRef } from "react";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import knight from "@/public/global/knight.png";
import { PageChallengeOldDesignSchema } from "@/sanity/pages/PageChallengeOldDesignTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import PaymentMethods from "./PaymentMethods";

export default function HeroSection({
  data,
  className,
  showTopBanner,
}: {
  data: PageChallengeOldDesignSchema;
  className?: string;
  showTopBanner?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}>
      <div className="relative mx-auto w-[min(229.25rem,100%)] overflow-clip rounded-[2rem] py-[13rem] lg:rounded-[5rem] lg:py-[15.75rem]">
        <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute bottom-0 left-1/2 !h-full !w-[max(100%,90rem)] !max-w-none -translate-x-1/2 object-cover object-top opacity-[.1] mix-blend-luminosity"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="relative z-10 container flex flex-col items-center px-[3rem]">
          {data?.heroChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}>
              <ChipSerif text={data.heroChip} />
            </motion.div>
          )}

          {data?.heroTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="richtext flex w-[min(92rem,100%)] flex-col gap-[2rem] prose-p:w-[min(64.75rem,100%)]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}>
              <CTAButton
                className="mt-[3rem]"
                data={data.heroCTA}
              />
            </motion.div>
          )}

          {!data?.hidePaymentMethods && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.7,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="mt-[3.5rem] w-full">
              <PaymentMethods />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
